import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import CategoryTable from './components/CategoryTable';
import CategoryToolbar from './components/CategoryToolbar';
import CategoryModal from './components/CategoryModal';
import CategoryDetailsModal from './components/CategoryDetailsModal';
import { useSelector } from 'react-redux';
import { fetchCategories, createCategory, updateCategory, deleteCategory } from 'services/categoryService';

const CategoryManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCategoryForEdit, setSelectedCategoryForEdit] = useState(null);
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState([]);
  const selectedBusiness = useSelector((state) => state?.business.selectedBusiness);

  const [categories, setCategories] = useState([]);


  const loadCategories = async ()=>{
    if(!selectedBusiness?.businessId) return;
    
    const response = await fetchCategories(selectedBusiness.businessId);
    if(response.success && response.data){
      setCategories(response.data);
    }

  }

  // Check authentication
  useEffect(() => {
    // const token = localStorage.getItem('authToken');
    // if (!token) {
    //   navigate('/login');
    // }
    loadCategories();
  }, []);

  const filteredCategory = categories?.filter(category => {
    const matchesSearch = category?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         category?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
  

    const matchesStatus = statusFilter === 'all' || category?.active === (statusFilter=='active'? true:false);
    
    return matchesSearch && matchesStatus;
  });

  const handleAddCategory = () => {
    setModalMode('add');
    setSelectedCategoryForEdit(null);
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    if(isDetailsModalOpen)
      setIsDetailsModalOpen(false);

    setModalMode('edit');
    setSelectedCategoryForEdit(category);
    setIsCategoryModalOpen(true);
  };

  const handleViewDetails = (category) => {
    setSelectedCategoryForDetails(category);
    setIsDetailsModalOpen(true);
  };

  const handleToggleStatus = (category) => {
    const newStatus = category?.status === 'active' ? 'inactive' : 'active';
    setCategories(prev => 
      prev?.map(c => 
        c?.id === category?.id 
          ? { ...c, status: newStatus }
          : c
      )
    );
  };

  const handleSaveCategory = async (categoryData) => {
    if (modalMode === 'add') {
      const response = await createCategory(selectedBusiness.businessId, categoryData);
      if(response.data && response.success){
        setCategories(prev => [...prev, { ...categoryData, id: Date.now() }]);
      }
    } else {
      const response = await updateCategory(selectedBusiness.businessId, categoryData.id, categoryData);
      if(response.data && response.success){
        setCategories(prev => 
          prev?.map(s => s?.id === categoryData?.id ? categoryData : s)
        );
      }
      
    }
  };

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(prev => 
      prev?.includes(categoryId)
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCategory?.length === filteredCategory?.length) {
      setSelectedCategory([]);
    } else {
      setSelectedCategory(filteredCategory?.map(category => category?.id));
    }
  };

  const handleBulkAction = (action) => {
    switch (action) {
      case 'activate':
        setCategories(prev => 
          prev?.map(s => 
            selectedCategory?.includes(c?.id) 
              ? { ...c, status: 'active' }
              : c
          )
        );
        break;
      case 'deactivate':
        setCategories(prev => 
          prev?.map(s => 
            selectedCategory?.includes(c?.id) 
              ? { ...c, status: 'inactive' }
              : c
          )
        );
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedCategory?.length} categories?`)) {
          setCategories(prev => 
            prev?.filter(s => !selectedCategory?.includes(s?.id))
          );
        }
        break;
    }
    setSelectedCategory([]);
  };

  return (
    <>
      <div className="p-4 lg:p-6">
        {/* Page Header */}
        <div className="mb-6">
          <BreadcrumbNavigation />
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mt-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground flex items-center">
                <Icon name="FolderCog" size={32} className="mr-3 text-primary" />
                Category Management
              </h1>
              <p className="text-muted-foreground mt-1">
                Manage your business categories
              </p>
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center space-x-6 text-sm">
              <div className="text-center">
                <div className="text-lg font-bold text-primary">
                  {categories?.filter(s => s?.status === 'active')?.length}
                </div>
                <div className="text-muted-foreground">Active</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-warning">
                  {categories?.filter(s => s?.status === 'inactive')?.length}
                </div>
                <div className="text-muted-foreground">Inactive</div>
              </div>

            </div>
          </div>
        </div>

        {/* Content Layout */}
        <div className="flex flex-col xl:flex-row gap-6">
          {/* Main Content Area */}
          <div className="flex-1 space-y-6">
            {/* Toolbar */}
            <CategoryToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              roleFilter={roleFilter}
              onRoleFilterChange={setRoleFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onAddCategory={handleAddCategory}
              selectedCount={selectedCategory?.length}
              onBulkAction={handleBulkAction}
              totalCount={filteredCategory?.length}
            />

            {/* Staff Table */}
            <CategoryTable
              categories={filteredCategory}
              onEdit={handleEditCategory}
              onViewDetails={handleViewDetails}
              onToggleStatus={handleToggleStatus}
              selectedCategory={selectedCategory}
              onSelectCategory={handleSelectCategory}
              onSelectAll={handleSelectAll}
              isAllSelected={selectedCategory?.length === filteredCategory?.length && filteredCategory?.length > 0}
            />
          </div>
        </div>
      </div>
      {/* Modals */}
      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        category={selectedCategoryForEdit}
        onSave={handleSaveCategory}
        mode={modalMode}
      />
      <CategoryDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        category={selectedCategoryForDetails}
        onEdit={handleEditCategory}
      />
    </>
  );
};

export default CategoryManagement;